/**
 * The Modeling pricing / cash-flow ASSUMPTIONS store — the single source of truth for
 * the inputs the Pricing and Cash-Flow panels both consume, so a change in one is
 * reflected in the other (one slice definition, one assumption set across the screen).
 *
 * Persistence is SERVER-BACKED, per (organization, user) — the `modeling_assumptions`
 * row behind GET/PUT `/api/modeling/assumptions` (mirroring the segment draft). This
 * store is the in-memory working copy only: `useModelingAssumptionsSync` (see
 * `use-modeling-assumptions.ts`) binds the active org, hydrates from the loader-warmed
 * server draft, and debounce-autosaves edits back. There is deliberately NO
 * localStorage layer — assumptions are tenant data that follow the user across
 * devices, not a browser preference.
 *
 * NAMESPACED PER ORGANIZATION (plan §6-D11): state lives in a `byOrg[organizationId]`
 * map keyed by the active org. Assumptions are org-scoped financial inputs — bank A's
 * FTP funding curve must never price bank B's portfolio — so until the org binding
 * lands the store serves the defaults and drops edits. Pre-bind reads fall back to the
 * loader-warmed draft in the query cache ({@link useModelingInputs}), so the SSR paint
 * and the client's hydration paint render the same saved assumptions with no flash.
 *
 * These are USER-CHOSEN assumptions only; the cohort-derived defaults (expected life,
 * lifetime loss, total balance, average term/rate) are read from the curves query at
 * render time and are deliberately NOT stored here, so they always track the active
 * segment rather than going stale against a saved slice.
 *
 * The FTP funding curve has NO default: empty means NOT PROVIDED, and the panels gate
 * their outputs until the user supplies one (manually or via CSV) — the engine's own
 * caller contract (`pricing.ts`) mandates it.
 */
import { useQueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type {
  AmountEntryMode,
  FundingCurvePoint,
  ModelingAssumptionsResponse,
  ModelingCustomLineItem,
  ModelingInputs,
} from '@repo/shared/common';

// The wire schema (contracts/modeling.ts) is the source of truth for the assumptions
// shape — the store re-exports its types so panels keep one import site and the store
// can never drift from what persists.
export type { AmountEntryMode, ModelingInputs };
export type CustomLineItem = ModelingCustomLineItem;

/** Query key for the loader-warmed per-user assumptions draft (see
 *  `use-modeling-assumptions.ts` for the queryOptions + sync hook). Defined here so the
 *  store's pre-bind fallback read and the sync layer share one key without a cycle. */
export const MODELING_ASSUMPTIONS_KEY = ['modeling', 'assumptions'] as const;

interface ModelingInputsState {
  /** The active organization id (bound from the current-org query; null until known).
   *  Bound fresh per mount so a stale org can never leak in. */
  readonly organizationId: string | null;
  /** Per-org working assumptions (only the orgs touched this session). */
  readonly byOrg: Readonly<Record<string, ModelingInputs>>;
  /** Bind the active organization (idempotent; changing org switches the assumptions). */
  readonly setOrganization: (organizationId: string | null) => void;
  /** Merge a partial assumptions patch. */
  readonly setInputs: (patch: Partial<ModelingInputs>) => void;
  /** Replace the funding (FTP) curve wholesale (manual-editor commit or CSV upload). */
  readonly setFundingCurve: (curve: readonly FundingCurvePoint[]) => void;
  /** Append a blank custom line item to the given build-up direction. */
  readonly addCustomLineItem: (kind: CustomLineItem['kind']) => void;
  /** Patch one custom line item by id (label, basis, value). */
  readonly updateCustomLineItem: (
    id: string,
    patch: Partial<Omit<CustomLineItem, 'id' | 'kind'>>,
  ) => void;
  /** Remove one custom line item by id. */
  readonly removeCustomLineItem: (id: string) => void;
  /** Replace the active org's assumptions (server-draft hydration; does not autosave). */
  readonly hydrate: (inputs: ModelingInputs) => void;
  /** Clear the active org back to the sensible defaults (funding curve back to
   *  NOT PROVIDED — there is no default curve). */
  readonly reset: () => void;
}

/** Sensible defaults for the user-chosen assumptions (cohort-derived ones come at
 *  render). The funding curve is EMPTY on purpose: not provided until the user
 *  supplies one, which gates the Pricing output and the Cash-Flow projection. */
export const DEFAULT_MODELING_INPUTS: ModelingInputs = {
  loanAmount: null,
  proposedNoteRatePct: null,
  // The residual return leg of the build-up = the target RETURN ON ASSETS (ROA). A
  // ~1% pre-tax ROA is the conventional target (a "target spread" it is not — that
  // reads as net interest margin, which is before credit + opex).
  targetSpreadPct: 1.0,
  originationCost: 1500,
  annualServicingCost: 250,
  contractualTermMonths: 60,
  upfrontFees: 0,
  annualRecurringFees: 0,
  originationCostMode: 'usd',
  annualServicingCostMode: 'usd',
  upfrontFeesMode: 'usd',
  annualRecurringFeesMode: 'usd',
  customLineItems: [],
  fundingCurve: [],
  lgdPct: 100,
  rarocEnabled: false,
  capitalAllocationPct: 9,
  targetPretaxRoePct: 15,
};

/** The active org's assumptions out of the map (defaults until the org is bound). */
function inputsOf(state: Pick<ModelingInputsState, 'organizationId' | 'byOrg'>): ModelingInputs {
  if (state.organizationId === null) return DEFAULT_MODELING_INPUTS;
  return state.byOrg[state.organizationId] ?? DEFAULT_MODELING_INPUTS;
}

export const useModelingInputsStore = create<ModelingInputsState>()((set) => {
  /** Apply `update` to the ACTIVE org's assumptions; a no-op until the org is
   *  bound (an edit with no tenant to attribute it to must never be persisted). */
  const updateInputs = (update: (inputs: ModelingInputs) => ModelingInputs): void => {
    set((state) => {
      if (state.organizationId === null) return state;
      return {
        byOrg: { ...state.byOrg, [state.organizationId]: update(inputsOf(state)) },
      };
    });
  };

  return {
    organizationId: null,
    byOrg: {},
    setOrganization: (organizationId) => set({ organizationId }),
    setInputs: (patch) => updateInputs((inputs) => ({ ...inputs, ...patch })),
    setFundingCurve: (curve) =>
      updateInputs((inputs) => ({ ...inputs, fundingCurve: [...curve] })),
    addCustomLineItem: (kind) =>
      updateInputs((inputs) => ({
        ...inputs,
        customLineItems: [
          ...inputs.customLineItems,
          { id: crypto.randomUUID(), label: '', kind, basis: 'usdAnnual', value: 0 },
        ],
      })),
    updateCustomLineItem: (id, patch) =>
      updateInputs((inputs) => ({
        ...inputs,
        customLineItems: inputs.customLineItems.map((item) =>
          item.id === id ? { ...item, ...patch } : item,
        ),
      })),
    removeCustomLineItem: (id) =>
      updateInputs((inputs) => ({
        ...inputs,
        customLineItems: inputs.customLineItems.filter((item) => item.id !== id),
      })),
    hydrate: (inputs) => updateInputs(() => inputs),
    reset: () => updateInputs(() => DEFAULT_MODELING_INPUTS),
  };
});

/**
 * The live assumptions for the active org, as the panels READ them. Wraps the store
 * read with a first-paint fallback: until `useModelingAssumptionsSync` binds the
 * active org, the (org-namespaced) store deliberately reads the defaults — so during
 * that pre-bind window serve the user's saved draft straight from the query cache
 * instead. The Modeling route loader-awaits the draft, so the SSR render and the
 * client's hydration render read the identical dehydrated value (no mismatch), and
 * the org-bind + hydrate effects land as visual no-ops. Once the org is bound the
 * store is authoritative, so org-switch behavior is untouched; with no cached draft
 * this is exactly the store read. (Mirrors `useActiveSegment`.)
 */
export function useModelingInputs(): ModelingInputs {
  const stored = useModelingInputsStore((state) => inputsOf(state));
  const bound = useModelingInputsStore((state) => state.organizationId !== null);
  const queryClient = useQueryClient();
  if (bound) return stored;
  const draft = queryClient.getQueryData<ModelingAssumptionsResponse>([
    ...MODELING_ASSUMPTIONS_KEY,
  ]);
  return draft?.assumptions ?? stored;
}

/** The assumptions-store actions, as a stable object. */
export const useModelingInputsActions = (): Pick<
  ModelingInputsState,
  | 'setInputs'
  | 'setFundingCurve'
  | 'addCustomLineItem'
  | 'updateCustomLineItem'
  | 'removeCustomLineItem'
  | 'reset'
> =>
  useModelingInputsStore(
    useShallow((state) => ({
      setInputs: state.setInputs,
      setFundingCurve: state.setFundingCurve,
      addCustomLineItem: state.addCustomLineItem,
      updateCustomLineItem: state.updateCustomLineItem,
      removeCustomLineItem: state.removeCustomLineItem,
      reset: state.reset,
    })),
  );
